import { Alert, Button, Flex, Form, Input } from "antd";
import Paragraph from "antd/es/typography/Paragraph";
import { Loader } from "Components/General/Loader";
import { ModalComponentProps } from "Components/General/Modal/ModalContext";
import { I18N_NS } from "Config/i18n";
import { API } from "General/API";
import { API_URL } from "General/APIEndpoints";
import { observer } from "mobx-react";
import { loginSettingsData } from "Pages/Login/Stores/LoginSettingsStore";
import { validateEmail } from "utils/validateEmail";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { loginStore } from "Stores";
import { checkPasswordComplexity, PasswordComplexity } from "utils/checkPasswordComplexity";
import { notificationError, notificationSuccess } from "utils/libHelpers/AntdNotification";

interface LinkToUnifiedAccountModalProps extends ModalComponentProps {
  loginInfo: API_LoginInformation;
  onPaveUserCreated?: (linkedAccountEmail: string) => void;
}

interface FormValues {
  linkedAccountEmail: string;
  firstName: string;
  lastName: string;
  pin: string;
  password: string;
  passwordConfirm: string;
}

type ValidateMailResponse = "OK" | "INVALID_DOMAIN" | "ALREADY_USED";

type ModalStep = "emailInput" | "accountExists" | "registration" | "pinVerification";

interface RegistrationData {
  firstName: string;
  lastName: string;
  password: string;
}

const COOLDOWN_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds

export const LinkToUnifiedAccountModal = observer(
  ({ hideModal, loginInfo, onPaveUserCreated }: LinkToUnifiedAccountModalProps) => {
    const { t } = useTranslation(I18N_NS.LOGIN);
    const [form] = Form.useForm<FormValues>();
    const [step, setStep] = useState<ModalStep>("emailInput");
    const [emailValidating, setEmailValidating] = useState(false);
    const [registrationData, setRegistrationData] = useState<RegistrationData | null>(null);
    const [password, setPassword] = useState("");
    const [pinButtonDisabled, setPinButtonDisabled] = useState(true);
    const [pinClickCount, setPinClickCount] = useState(0);
    const [cooldownEndTime, setCooldownEndTime] = useState<number | null>(null);
    const [remainingTime, setRemainingTime] = useState(0);
    const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

    // Timer effect for cooldown countdown
    useEffect(() => {
      if (cooldownEndTime !== null) {
        const updateRemainingTime = () => {
          const now = Date.now();
          const remaining = Math.max(0, cooldownEndTime - now);
          setRemainingTime(remaining);

          if (remaining === 0) {
            setCooldownEndTime(null);
            setPinClickCount(0);
            if (timerRef.current) {
              clearInterval(timerRef.current);
              timerRef.current = null;
            }
          }
        };

        updateRemainingTime();
        timerRef.current = setInterval(updateRemainingTime, 1000);
      }

      return () => {
        if (timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }
      };
    }, [cooldownEndTime]);

    const formatRemainingTime = useCallback((ms: number) => {
      const totalSeconds = Math.ceil(ms / 1000);
      const minutes = Math.floor(totalSeconds / 60);
      const seconds = totalSeconds % 60;
      return `${minutes}:${seconds.toString().padStart(2, "0")}`;
    }, []);

    const validateLinkedAccountEmail = useCallback(
      (_rule: unknown, value?: string) => {
        if (!value || validateEmail(value)) {
          return Promise.resolve();
        }

        return Promise.reject(new Error(t("Please enter your email address")));
      },
      [t]
    );

    const handleValidateEmail = async () => {
      await form.validateFields(["linkedAccountEmail"]);
      const linkedAccountEmail = form.getFieldValue("linkedAccountEmail");
      if (!validateEmail(linkedAccountEmail)) return;

      setEmailValidating(true);
      try {
        const validationResult: ValidateMailResponse = await API.newData(
          API_URL.PAVE_USERS.validateMail(),
          linkedAccountEmail,
          null,
          {
            headers: {
              Authorization: loginInfo.jwtToken,
            },
          }
        );
        if (validationResult === "INVALID_DOMAIN") {
          form.setFields([{ name: "linkedAccountEmail", errors: [t("Invalid email domain")] }]);
        } else if (validationResult === "ALREADY_USED") {
          setStep("accountExists");
          setPinButtonDisabled(false);
        } else if (validationResult === "OK") {
          setStep("registration");
          setPinButtonDisabled(false);
        } else {
          throw new Error("Invalid validation result: " + validationResult);
        }
      } finally {
        setEmailValidating(false);
      }
    };

    const handleSendPin = async () => {
      const email = form.getFieldValue("linkedAccountEmail");
      if (!validateEmail(email)) return;

      const newClickCount = pinClickCount + 1;
      setPinClickCount(newClickCount);

      await sendPAVEUserMail(email, pinClickCount > 0);

      // Start cooldown after second click
      if (newClickCount >= 2) {
        const endTime = Date.now() + COOLDOWN_DURATION;
        setCooldownEndTime(endTime);
      }
    };

    //  Get the user's email and validate it with sendPAVEUserMail. If code is correct - move to step 2.
    // // If code is incorrect - show error message and stay on step 1.
    //  Enter password using ConfirmPassword component. If password is correct - move to step 3
    // Show success message and hide modal. Tell that user can login with PAVE account now.
    // // Enable unifiedAccount login mode, override "save username" with the email if it was enabled

    const onValuesChange = (changedFields: FormValues, allFields: FormValues) => {
      if (changedFields.password) {
        setPassword(changedFields.password);
      }
      if (step === "emailInput") {
        setPinButtonDisabled(!validateEmail(allFields.linkedAccountEmail));
      }
    };

    const sendPAVEUserMail = async (mailAddress: string, refresh?: boolean) => {
      const firstName = registrationData?.firstName ?? form.getFieldValue("firstName");
      const lastName = registrationData?.lastName ?? form.getFieldValue("lastName");
      const payload: API_AccountCreationRequest = {
        mailAddress,
        firstName,
        lastName,
        refresh: refresh,
      };
      await API.newData(API_URL.PAVE_USERS.sendPAVEUserMail(), payload, null, {
        headers: {
          Authorization: loginInfo.jwtToken,
        },
      });
    };

    const handleFinish = async (values: FormValues) => {
      if (step === "emailInput") {
        return;
      }

      if (step === "accountExists") {
        try {
          const payload: API_PAVEUserCredentials = {
            username: values.linkedAccountEmail,
            password: values.password,
          };
          await API.newData(API_URL.PAVE_USERS.linkUser(), payload, null, {
            headers: {
              Authorization: loginInfo.jwtToken,
            },
          });
          notificationSuccess(t("Success"), t("Your account is now linked to a PAVE account"));
          hideModal();
          await loginStore.login({
            userId: values.linkedAccountEmail,
            password: values.password,
            unifiedAccount: true,
          });
          loginStore.setPassword(values.password);
        } catch (error) {
          if (error.response && error.response.status === 401) {
            form.setFields([{ name: "password", errors: [t("Invalid password")] }]);
          } else {
            notificationError(t("Error"), t("Failed to link your account to a PAVE account"));
          }
          throw error;
        }
        return;
      }

      if (step === "registration") {
        setRegistrationData({
          firstName: values.firstName,
          lastName: values.lastName,
          password: values.password,
        });
        await handleSendPin();
        setStep("pinVerification");
        setPinClickCount(1);
        setCooldownEndTime(null);
        setRemainingTime(0);
        form.focusField("pin");
        return;
      }

      if (!registrationData) return;

      const { linkedAccountEmail, pin, password } = values;
      const payload: API_PAVEUserCreateRequest = {
        username: linkedAccountEmail,
        firstName: registrationData.firstName,
        lastName: registrationData.lastName,
        pin,
        password: registrationData.password || password,
      };
      await API.newData(API_URL.PAVE_USERS.createPAVEUser(), payload, null, {
        headers: {
          Authorization: loginInfo.jwtToken,
        },
      });
      notificationSuccess(t("Success"), t("Your account is now linked to a PAVE account"));
      onPaveUserCreated?.(values.linkedAccountEmail);
      hideModal();
    };

    const checkPassword = useCallback(
      (_rule, value: string) => {
        if (!value) {
          return Promise.reject();
        }
        const { isValid } = checkPasswordComplexity(value, loginSettingsData.passwordPolicy ?? {});
        if (isValid) {
          return Promise.resolve();
        }
        return Promise.reject(t("Password does not meet the security requirements"));
      },
      [t]
    );

    useEffect(() => {
      (async () => {
        // If you hand over a positive ID, it must match the current user-ID, otherwise we throw an error 403.
        // If you hand over -1 we return the exact same response as if you provided the current user-ID.
        const response: API_PasswordPolicy = await API.getData(
          API_URL.PASSWORD.getPasswordPolicyForUserByKey(-1),
          null,
          {
            headers: {
              Authorization: loginInfo.jwtToken,
            },
          }
        );
        loginSettingsData.setPasswordDefinitions(response);
      })();
      return () => {
        loginSettingsData.clearPasswordPolicy();
      };
    }, []);

    return (
      <Form
        form={form}
        onValuesChange={onValuesChange}
        layout="vertical"
        requiredMark={false}
        autoComplete="off"
        onFinish={handleFinish}
      >
        <Flex gap={16} align="flex-end">
          <Form.Item
            style={{ flexGrow: 1 }}
            name="linkedAccountEmail"
            label={t("Email")}
            rules={[
              { required: true, message: t("Please enter your email address") },
              { validator: validateLinkedAccountEmail },
            ]}
          >
            <Input
              type="email"
              placeholder={t("Email address")}
              autoComplete="off"
              disabled={step !== "emailInput"}
              onPressEnter={() => {
                if (step === "emailInput") {
                  handleValidateEmail();
                }
              }}
            />
          </Form.Item>
          {step === "emailInput" && (
            <Form.Item style={{ flexShrink: 0 }}>
              <Button
                type="primary"
                htmlType="button"
                disabled={pinButtonDisabled}
                loading={emailValidating}
                onClick={handleValidateEmail}
              >
                {t("Continue")}
              </Button>
            </Form.Item>
          )}
        </Flex>

        {step === "accountExists" && (
          <>
            <Alert
              type="info"
              title={t(
                // eslint-disable-next-line max-len
                "This email address is already in use. Please enter your password to link your account to a PAVE account."
              )}
              className="mb-2"
            />
            <Form.Item
              name="password"
              label={t("Password")}
              rules={[{ required: true, message: t("Please enter your password") }]}
            >
              <Input.Password type="password" placeholder={t("Password")} autoComplete="off" />
            </Form.Item>
          </>
        )}

        {step === "registration" && (
          <>
            <Form.Item
              name="firstName"
              label={t("First name")}
              rules={[{ required: true, message: t("Please enter your first name") }]}
            >
              <Input placeholder={t("First name")} autoComplete="off" />
            </Form.Item>
            <Form.Item
              name="lastName"
              label={t("Last name")}
              rules={[{ required: true, message: t("Please enter your last name") }]}
            >
              <Input placeholder={t("Last name")} autoComplete="off" />
            </Form.Item>
            <Form.Item
              name="password"
              label={t("Password")}
              rules={[{ required: true, message: t("Please enter new password") }, { validator: checkPassword }]}
            >
              <Input.Password type="password" placeholder={t("Password")} autoComplete="off" />
            </Form.Item>
            <Form.Item
              name="passwordConfirm"
              label={t("Confirm new password")}
              dependencies={["password"]}
              rules={[
                {
                  required: true,
                },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue("password") === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error(t("The new password that you entered do not match")));
                  },
                }),
              ]}
            >
              <Input.Password type="password" placeholder={t("Confirm new password")} autoComplete="off" />
            </Form.Item>
            {loginSettingsData.passwordPolicy ? <PasswordComplexity inputValue={password} /> : <Loader />}
          </>
        )}

        {step === "pinVerification" && (
          <>
            <Paragraph>
              {t("A PIN has been sent to your email address. Please enter it to link your account to a PAVE account.")}
            </Paragraph>
            <Flex gap={16} justify="center" align="flex-end">
              <Form.Item name="pin" rules={[{ required: true, message: t("Please enter pin from your email") }]}>
                <Input.OTP length={6} />
              </Form.Item>
              <Form.Item style={{ flexShrink: 0 }}>
                <Button
                  type="text"
                  htmlType="button"
                  disabled={pinButtonDisabled || cooldownEndTime !== null}
                  onClick={handleSendPin}
                >
                  {cooldownEndTime === null
                    ? t("Resend pin")
                    : t("Resend pin ({{time}})", { time: formatRemainingTime(remainingTime) })}
                </Button>
              </Form.Item>
            </Flex>
          </>
        )}

        {step !== "emailInput" && (
          <div className="text-right">
            <Button type="primary" htmlType={"submit"}>
              {t("Link account")}
            </Button>
          </div>
        )}
      </Form>
    );
  }
);
